import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, Send, Loader2, Heart, Wind, Sparkles, Crown, Leaf, Compass, Sun, Shuffle, Moon, Zap, Cloud, CloudRain, HelpCircle, Feather, Sunrise, Users, Eye, Search, Pause, Activity, Frown, Minus, SkipForward, Bookmark, X, Flame, Coffee, Droplet, Anchor, Target, Shield, Music, Star } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import Footer from "../components/Footer";

// Mode icon mapping
const MODE_ICONS = {
  leaf: Leaf,
  heart: Heart,
  compass: Compass,
  sparkles: Sparkles,
  sun: Sun,
  shuffle: Shuffle,
};

// Ritual icon mapping
const RITUAL_ICONS = {
  wind: Wind,
  droplet: Droplet,
  flame: Flame,
  heart: Heart,
  sparkles: Sparkles,
  coffee: Coffee,
  anchor: Anchor,
  sun: Sun,
  feather: Feather,
  scroll: Feather,
  eye: Eye,
  target: Target,
  pause: Pause,
  shield: Shield,
  music: Music,
  star: Star,
  zap: Zap
};

// Mood tag icon mapping
const MOOD_ICONS = {
  moon: Moon,
  "battery-low": Minus,
  cloud: Cloud,
  minus: Minus,
  zap: Zap,
  waves: Wind,
  wind: Wind,
  activity: Activity,
  "cloud-rain": CloudRain,
  heart: Heart,
  droplet: CloudRain,
  frown: Frown,
  "help-circle": HelpCircle,
  pause: Pause,
  search: Search,
  eye: Eye,
  feather: Feather,
  sunrise: Sunrise,
  users: Users,
  sun: Sun,
  sparkles: Sparkles,
};

export default function CheckIn({ auth }) {
  const navigate = useNavigate();
  const [modes, setModes] = useState([]);
  const [moodTags, setMoodTags] = useState({});
  const [timeContext, setTimeContext] = useState(null);
  const [selectedMode, setSelectedMode] = useState(null);
  const [selectedMoods, setSelectedMoods] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [currentResponse, setCurrentResponse] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [step, setStep] = useState("context_select"); // context_select (Inner Circle only), mood_select, mode_select, prompts, processing, response, limit_reached
  const [limitInfo, setLimitInfo] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [skippedPromptIds, setSkippedPromptIds] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [savedPromptIds, setSavedPromptIds] = useState(new Set());
  const [recommendedRituals, setRecommendedRituals] = useState([]);
  
  // Session context state (What's brought you here)
  const [sessionContext, setSessionContext] = useState(null);
  
  // Affirmation state
  const [affirmation, setAffirmation] = useState(null);
  const [affirmationLoading, setAffirmationLoading] = useState(false);
  const [affirmationOffered, setAffirmationOffered] = useState(false);
  const [affirmationSaved, setAffirmationSaved] = useState(false);
  const [showAffirmationOffer, setShowAffirmationOffer] = useState(false);

  // Session context options
  const SESSION_CONTEXT_OPTIONS = [
    { id: "before_conversation", label: "Before a conversation" },
    { id: "after_something_hard", label: "After something hard" },
    { id: "end_of_day", label: "End of the day" },
    { id: "feeling_stuck", label: "Feeling stuck" },
    { id: "needing_steadiness", label: "Needing steadiness" },
    { id: "just_checking_in", label: "Just checking in" },
    { id: "feeling_good", label: "Feeling good" }
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Get local time-based greeting
  const getLocalGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return "Good morning";
    if (hour >= 12 && hour < 17) return "Good afternoon";
    if (hour >= 17 && hour < 21) return "Good evening";
    return "Late night check-in";
  };

  const fetchInitialData = async () => {
    try {
      const [modesRes, moodsRes, timeRes, subRes, savedRes] = await Promise.all([
        fetch(`${API}/prompts/modes`, { credentials: 'include' }),
        fetch(`${API}/prompts/mood-tags`, { credentials: 'include' }),
        fetch(`${API}/prompts/time-context`, { credentials: 'include' }),
        fetch(`${API}/subscription/status`, { credentials: 'include' }),
        fetch(`${API}/prompts/saved`, { credentials: 'include' })
      ]);

      if (modesRes.ok) setModes(await modesRes.json());
      if (moodsRes.ok) setMoodTags(await moodsRes.json());
      if (timeRes.ok) {
        const timeData = await timeRes.json();
        // Override greeting with local time
        setTimeContext({
          ...timeData,
          greeting: getLocalGreeting()
        });
      }
      if (savedRes.ok) {
        const savedData = await savedRes.json();
        setSavedPromptIds(new Set(savedData.saved_prompts?.map(p => p.prompt_id) || []));
      }
      if (subRes.ok) {
        const subData = await subRes.json();
        setSubscriptionStatus(subData);
        // Set initial step - Inner Circle users get context_select first
        if (subData.plan !== 'free') {
          setStep("context_select");
        } else {
          setStep("mood_select");
        }
      } else {
        setStep("mood_select");
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setStep("mood_select");
    } finally {
      setLoading(false);
    }
  };

  // Toggle save/unsave prompt
  const toggleSavePrompt = async (promptId) => {
    const isSaved = savedPromptIds.has(promptId);
    
    try {
      if (isSaved) {
        const response = await fetch(`${API}/prompts/save/${promptId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        if (response.ok) {
          setSavedPromptIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(promptId);
            return newSet;
          });
          toast("Prompt removed from saved", { duration: 2000 });
        }
      } else {
        const response = await fetch(`${API}/prompts/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ prompt_id: promptId })
        });
        if (response.ok) {
          setSavedPromptIds(prev => new Set([...prev, promptId]));
          toast("Prompt saved!", { duration: 2000 });
        }
      }
    } catch (error) {
      console.error('Error toggling save:', error);
    }
  };

  // Track prompt response for analytics
  const trackPromptResponse = async (promptId, category) => {
    try {
      await fetch(`${API}/prompts/track-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ prompt_id: promptId, category })
      });
    } catch (error) {
      console.error('Error tracking response:', error);
    }
  };

  // Fetch affirmation for premium users
  const fetchAffirmation = async (userMessage) => {
    // Only offer to premium users
    if (!subscriptionStatus || subscriptionStatus.plan === 'free') {
      return;
    }
    
    setAffirmationLoading(true);
    try {
      const response = await fetch(`${API}/affirmations/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          user_message: userMessage,
          mood_tags: selectedMoods,
          ai_response_sections: aiResponse?.sections || {}
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.offered && data.affirmation) {
          setAffirmation(data.affirmation);
          setShowAffirmationOffer(true);
        } else {
          // Affirmation was not offered - show gentle message
          toast("Sometimes stillness is its own gift. No affirmation needed right now.", { duration: 4000 });
        }
        setAffirmationOffered(true);
      }
    } catch (error) {
      console.error('Error generating affirmation:', error);
      toast.error("Couldn't generate affirmation");
    } finally {
      setAffirmationLoading(false);
    }
  };

  const handleSaveAffirmation = async () => {
    if (!affirmation) return;
    
    try {
      const response = await fetch(`${API}/affirmations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ affirmation })
      });
      
      if (response.ok) {
        setAffirmationSaved(true);
        toast.success("Affirmation saved");
      }
    } catch (error) {
      toast.error("Couldn't save affirmation");
    }
  };

  const handleDeclineAffirmation = () => {
    setShowAffirmationOffer(false);
  };

  const fetchPrompts = async (mode = null, moods = []) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/prompts/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          mode, 
          mood_tags: moods.length > 0 ? moods : null,
          use_time_of_day: true,
          session_context: sessionContext  // Pass "What's brought you here" context
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setPrompts(data.prompts);
        setSessionInfo(data);
        setStep("prompts");
      } else {
        console.error('Error response:', data);
        toast.error("Couldn't load prompts");
      }
    } catch (error) {
      console.error('Error fetching prompts:', error);
      toast.error("Couldn't load prompts");
    } finally {
      setLoading(false);
    }
  };

  const handleMoodToggle = (moodId) => {
    setSelectedMoods(prev => {
      if (prev.includes(moodId)) {
        return prev.filter(m => m !== moodId);
      }
      // Limit to 3 moods
      if (prev.length >= 3) {
        toast("You can select up to 3 feelings", { duration: 2000 });
        return prev;
      }
      return [...prev, moodId];
    });
  };

  const handleMoodContinue = () => {
    setStep("mode_select");
  };

  // Gentle closing messages
  const closingMessages = [
    "You can carry this gently.",
    "Nothing else is required right now.",
    "You can return when it feels supportive.",
    "This moment is enough.",
    "Rest here as long as you need."
  ];
  
  const getRandomClosingMessage = () => {
    return closingMessages[Math.floor(Math.random() * closingMessages.length)];
  };

  const handleSkipMoods = () => {
    setStep("mode_select");
  };

  const handleModeSelect = (modeId) => {
    setSelectedMode(modeId);
    fetchPrompts(modeId, selectedMoods);
  };

  const handleSkipModeSelect = () => {
    fetchPrompts(null, selectedMoods);
  };

  const handleSkipPrompt = async () => {
    if (skipping) return;
    
    const currentPrompt = prompts[currentPromptIndex];
    if (!currentPrompt) return;
    
    setSkipping(true);
    
    try {
      const response = await fetch(`${API}/prompts/skip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          prompt_id: currentPrompt.prompt_id,
          category: currentPrompt.category,
          excluded_prompt_ids: [...prompts.map(p => p.prompt_id), ...skippedPromptIds]
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Replace the current prompt with the new one
        const newPrompts = [...prompts];
        newPrompts[currentPromptIndex] = data.prompt;
        setPrompts(newPrompts);
        setSkippedPromptIds(prev => [...prev, currentPrompt.prompt_id]);
        setCurrentResponse(""); // Clear any partial response
        toast("Here's a different prompt", { duration: 2000 });
      } else {
        console.error('Skip error:', data);
        toast.error("Couldn't find another prompt");
      }
    } catch (error) {
      console.error('Error skipping prompt:', error);
      toast.error("Something went wrong");
    } finally {
      setSkipping(false);
    }
  };

  const handleNext = () => {
    if (!currentResponse.trim()) {
      toast.error("Please share something before continuing");
      return;
    }

    // Track the response for analytics
    const currentPrompt = prompts[currentPromptIndex];
    if (currentPrompt) {
      trackPromptResponse(currentPrompt.prompt_id, currentPrompt.category);
    }

    setResponses(prev => ({
      ...prev,
      [prompts[currentPromptIndex].prompt_id]: currentResponse
    }));

    if (currentPromptIndex < prompts.length - 1) {
      setCurrentPromptIndex(prev => prev + 1);
      setCurrentResponse("");
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setStep("processing");
    setSubmitting(true);

    const allResponses = {
      ...responses,
      [prompts[currentPromptIndex].prompt_id]: currentResponse
    };

    const message = prompts.map(p => {
      const response = allResponses[p.prompt_id] || "";
      return `${p.text}\n${response}`;
    }).join("\n\n");

    const promptIds = prompts.map(p => p.prompt_id);
    const promptCategories = prompts.map(p => p.category);

    try {
      const response = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          message, 
          prompt_ids: promptIds,
          mood_tags: selectedMoods,
          prompt_categories: promptCategories,
          session_context: sessionContext
        })
      });

      // Read the response text first, then parse
      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Response parse error:', parseError, 'Response text:', responseText);
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        if (response.status === 403 && data.detail?.error === 'check_in_limit_reached') {
          setLimitInfo(data.detail);
          setStep("limit_reached");
          return;
        }
        
        throw new Error(data.detail?.message || data.detail || 'Failed to get response');
      }

      setAiResponse(data.response);
      setStep("response");
      
      // Fetch mood-based ritual recommendations
      fetchMoodRituals();
    } catch (error) {
      console.error('Error submitting check-in:', error);
      toast.error(error.message || "Something went wrong. Please try again.");
      setStep("prompts");
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch rituals based on user's selected moods
  const fetchMoodRituals = async () => {
    try {
      const response = await fetch(`${API}/rituals/for-mood`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mood_tags: selectedMoods })
      });
      if (response.ok) {
        const data = await response.json();
        setRecommendedRituals(data.rituals || []);
      }
    } catch (error) {
      console.error('Error fetching mood rituals:', error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.metaKey) {
      handleNext();
    }
  };

  const resetCheckIn = () => {
    // Reset to context_select for Inner Circle users, mood_select for free users
    if (subscriptionStatus && subscriptionStatus.plan !== 'free') {
      setStep("context_select");
    } else {
      setStep("mood_select");
    }
    setSessionContext(null);
    setSelectedMode(null);
    setSelectedMoods([]);
    setPrompts([]);
    setCurrentPromptIndex(0);
    setResponses({});
    setCurrentResponse("");
    setAiResponse(null);
    setSessionInfo(null);
    setSkippedPromptIds([]);
    setRecommendedRituals([]);
  };

  if (loading && step === "mood_select") {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-12 h-12 border-2 border-[#A87C6D] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#4A4744] font-light">Preparing your space...</p>
        </div>
      </div>
    );
  }

  // Context Selection (Inner Circle only) - What's brought you here
  if (step === "context_select") {
    return (
      <div className="min-h-screen bg-[#FDFBF7]" data-testid="context-select-view">
        <nav className="px-6 py-4 border-b border-[#E6E2DD]">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <Link to="/dashboard">
              <Button variant="ghost" className="rounded-full text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
            </Link>
            <span 
              className="text-xl font-light text-[#2D2A26]"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Check-in
            </span>
            <div className="w-20"></div>
          </div>
        </nav>

        <main className="max-w-xl mx-auto px-6 py-10">
          <div className="text-center mb-10 animate-fade-in">
            {timeContext && (
              <p className="text-[#A87C6D] text-sm mb-2">{timeContext.greeting}</p>
            )}
            <h2 
              className="text-2xl md:text-3xl font-light text-[#2D2A26] mb-3"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              What's brought you here?
            </h2>
            <p className="text-[#4A4744] font-light text-sm">
              This helps tailor your experience. Feel free to skip.
            </p>
          </div>

          <div className="space-y-3 animate-slide-up">
            {SESSION_CONTEXT_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => {
                  setSessionContext(option.id);
                  setStep("mood_select");
                }}
                className="w-full p-4 rounded-2xl border border-[#E6E2DD] bg-white hover:border-[#A87C6D] hover:bg-[#A87C6D]/5 transition-all duration-200 text-left group"
                data-testid={`context-${option.id}`}
              >
                <span className="text-[#2D2A26] font-light group-hover:text-[#A87C6D] transition-colors">
                  {option.label}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-8 text-center animate-fade-in">
            <Button
              onClick={() => {
                setSessionContext(null);
                setStep("mood_select");
              }}
              variant="ghost"
              className="rounded-full px-6 text-[#6E6A66] hover:text-[#4A4744] hover:bg-[#E6E2DD]"
              data-testid="skip-context-btn"
            >
              Skip this step
            </Button>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // Mood Selection
  if (step === "mood_select") {
    const handleMoodBack = () => {
      // Inner Circle users go back to context_select, free users go to dashboard
      if (subscriptionStatus && subscriptionStatus.plan !== 'free') {
        setStep("context_select");
      } else {
        navigate('/dashboard');
      }
    };

    return (
      <div className="min-h-screen bg-[#FDFBF7]" data-testid="mood-select-view">
        <nav className="px-6 py-4 border-b border-[#E6E2DD]">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={handleMoodBack}
              className="rounded-full text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]" 
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <span 
              className="text-xl font-light text-[#2D2A26]"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Check-in
            </span>
            <div className="w-20"></div>
          </div>
        </nav>

        <main className="max-w-xl mx-auto px-6 py-10">
          <div className="text-center mb-8 animate-fade-in">
            {timeContext && (
              <p className="text-[#A87C6D] text-sm mb-2">{timeContext.greeting}</p>
            )}
            <h2 
              className="text-2xl md:text-3xl font-light text-[#2D2A26] mb-3"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              How are you feeling in this moment?
            </h2>
            <p className="text-[#4A4744] font-light text-sm">
              Select up to 3 feelings, or skip if you're not sure.
            </p>
          </div>

          <div className="space-y-6 animate-slide-up">
            {Object.entries(moodTags).map(([groupKey, group]) => (
              <div key={groupKey}>
                <p className="text-xs uppercase tracking-wider text-[#6E6A66] mb-2">{group.label}</p>
                <div className="flex flex-wrap gap-2">
                  {group.tags.map((tag) => {
                    const IconComponent = MOOD_ICONS[tag.icon] || Heart;
                    const isSelected = selectedMoods.includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        onClick={() => handleMoodToggle(tag.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all duration-200 ${
                          isSelected 
                            ? 'bg-[#A87C6D] text-white' 
                            : 'bg-[#F5F2ED] text-[#4A4744] hover:bg-[#E6E2DD]'
                        }`}
                        data-testid={`mood-${tag.id}`}
                      >
                        <IconComponent className="w-4 h-4" />
                        {tag.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col items-center gap-3 animate-slide-up delay-300" style={{ animationFillMode: 'forwards', opacity: 0 }}>
            <Button
              onClick={handleMoodContinue}
              className="rounded-full px-8 py-5 bg-[#A87C6D] hover:bg-[#956d5f] text-white"
              data-testid="mood-continue-btn"
            >
              Continue {selectedMoods.length > 0 && `(${selectedMoods.length})`}
            </Button>
            <button
              onClick={handleSkipMoods}
              className="text-[#6E6A66] hover:text-[#4A4744] text-sm"
              data-testid="skip-moods-btn"
            >
              I'm not sure
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Mode Selection
  if (step === "mode_select") {
    return (
      <div className="min-h-screen bg-[#FDFBF7]" data-testid="mode-select-view">
        <nav className="px-6 py-4 border-b border-[#E6E2DD]">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => setStep("mood_select")}
              className="rounded-full text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]" 
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <span 
              className="text-xl font-light text-[#2D2A26]"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Check-in
            </span>
            <div className="w-20"></div>
          </div>
        </nav>

        <main className="max-w-xl mx-auto px-6 py-12">
          {selectedMoods.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center mb-6 animate-fade-in">
              {selectedMoods.map(mood => {
                const allTags = Object.values(moodTags).flatMap(g => g.tags);
                const tag = allTags.find(t => t.id === mood);
                return tag ? (
                  <span key={mood} className="px-3 py-1 rounded-full bg-[#A87C6D]/10 text-[#A87C6D] text-sm">
                    {tag.label}
                  </span>
                ) : null;
              })}
            </div>
          )}

          <div className="text-center mb-10 animate-fade-in">
            <h2 
              className="text-2xl md:text-3xl font-light text-[#2D2A26] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              What do you need?
            </h2>
            <p className="text-[#4A4744] font-light">
              Choose a focus, or let me guide you.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-8 animate-slide-up">
            {modes.map((mode, idx) => {
              const IconComponent = MODE_ICONS[mode.icon] || Heart;
              return (
                <button
                  key={mode.id}
                  onClick={() => handleModeSelect(mode.id)}
                  className={`paper-card rounded-2xl p-5 text-center hover-lift cursor-pointer transition-all duration-300 ${
                    selectedMode === mode.id ? 'ring-2 ring-[#A87C6D]' : ''
                  }`}
                  data-testid={`mode-${mode.id}`}
                  style={{ animationDelay: `${idx * 50}ms` }}
                >
                  <IconComponent className="w-6 h-6 text-[#A87C6D] mx-auto mb-2" />
                  <p className="text-sm font-medium text-[#2D2A26]">{mode.label}</p>
                </button>
              );
            })}
          </div>

          <div className="text-center animate-slide-up delay-300" style={{ animationFillMode: 'forwards', opacity: 0 }}>
            <button
              onClick={handleSkipModeSelect}
              className="text-[#6E6A66] hover:text-[#4A4744] text-sm underline"
              data-testid="skip-mode-btn"
            >
              Just start
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Loading prompts
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-12 h-12 border-2 border-[#A87C6D] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#4A4744] font-light">Preparing your prompts...</p>
        </div>
      </div>
    );
  }

  // Limit Reached state
  if (step === "limit_reached") {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6">
        <div className="text-center max-w-md animate-fade-in">
          <div className="w-20 h-20 rounded-full bg-[#FEF3CD] flex items-center justify-center mx-auto mb-8">
            <Crown className="w-10 h-10 text-[#B8860B]" />
          </div>
          <h2 
            className="text-2xl font-normal text-[#2D2A26] mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            You've reached your weekly limit
          </h2>
          <p className="text-[#4A4744] font-light mb-2">
            You've used {limitInfo?.check_ins_used || 3} of {limitInfo?.check_in_limit || 3} free check-ins this week.
          </p>
          <p className="text-[#4A4744] font-light mb-8">
            Upgrade to Inner Circle for unlimited check-ins and deeper emotional insights.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/subscription')}
              className="rounded-full px-8 py-5 bg-[#A87C6D] hover:bg-[#956d5f] text-white"
              data-testid="upgrade-btn"
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to Inner Circle
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="rounded-full px-8 py-5 border-[#E6E2DD] text-[#4A4744]"
            >
              Return to dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Processing state
  if (step === "processing") {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6">
        <div className="text-center max-w-md animate-fade-in">
          <div className="w-20 h-20 rounded-full bg-[#A87C6D]/10 flex items-center justify-center mx-auto mb-8 animate-breathe">
            <Heart className="w-10 h-10 text-[#A87C6D]" />
          </div>
          <h2 
            className="text-2xl font-normal text-[#2D2A26] mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Taking a moment...
          </h2>
          <p className="text-[#4A4744] font-light">
            Reflecting on what you've shared.
          </p>
        </div>
      </div>
    );
  }

  // AI Response state
  if (step === "response" && aiResponse) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]" data-testid="ai-response-view">
        <nav className="px-6 py-4 border-b border-[#E6E2DD]">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <Link to="/dashboard">
              <Button variant="ghost" className="rounded-full text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
            </Link>
            <span 
              className="text-xl font-light text-[#2D2A26]"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Your reflection
            </span>
            <div className="w-20"></div>
          </div>
        </nav>

        <main className="max-w-2xl mx-auto px-6 py-12">
          <div className="animate-fade-in">
            {aiResponse.sections?.reflect && (
              <div className="ai-section reflect animate-slide-up delay-100 mb-6" data-testid="reflect-section" style={{ animationFillMode: 'forwards', opacity: 0 }}>
                <div className="flex items-center gap-2 mb-1">
                  <Heart className="w-5 h-5 text-[#A87C6D]" />
                  <h3 className="text-lg font-medium text-[#A87C6D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Reflect
                  </h3>
                </div>
                <p className="text-xs text-[#6E6A66] mb-3 ml-7 italic">What is present</p>
                <p className="text-[#2D2A26] font-light leading-relaxed whitespace-pre-line">
                  {aiResponse.sections.reflect}
                </p>
              </div>
            )}

            {aiResponse.sections?.regulate && (
              <div className="ai-section regulate animate-slide-up delay-200 mb-6" data-testid="regulate-section" style={{ animationFillMode: 'forwards', opacity: 0 }}>
                <div className="flex items-center gap-2 mb-1">
                  <Wind className="w-5 h-5 text-[#7A9E7E]" />
                  <h3 className="text-lg font-medium text-[#7A9E7E]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Regulate
                  </h3>
                </div>
                <p className="text-xs text-[#6E6A66] mb-3 ml-7 italic">To calm your nervous system or celebrate your joy</p>
                <p className="text-[#2D2A26] font-light leading-relaxed whitespace-pre-line">
                  {aiResponse.sections.regulate}
                </p>
              </div>
            )}

            {aiResponse.sections?.redirect && aiResponse.sections.redirect.trim() && (
              <div className="ai-section redirect animate-slide-up delay-400 mb-8" data-testid="redirect-section" style={{ animationFillMode: 'forwards', opacity: 0 }}>
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-5 h-5 text-[#7D98A1]" />
                  <h3 className="text-lg font-medium text-[#7D98A1]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Redirect
                  </h3>
                </div>
                <p className="text-xs text-[#6E6A66] mb-3 ml-7 italic">A gentle or playful suggestion</p>
                <p className="text-[#2D2A26] font-light leading-relaxed whitespace-pre-line">
                  {aiResponse.sections.redirect}
                </p>
              </div>
            )}

            {/* Mood-Based Ritual Recommendation */}
            {recommendedRituals.length > 0 && (
              <div className="mb-8 animate-slide-up delay-350" style={{ animationFillMode: 'forwards', opacity: 0 }}>
                <h3 className="text-sm font-medium text-[#6E6A66] uppercase tracking-wider mb-4">
                  A Ritual for You 🌿
                </h3>
                {recommendedRituals.slice(0, 1).map((ritual) => {
                  const RitualIcon = RITUAL_ICONS[ritual.icon] || Sparkles;
                  return (
                    <div 
                      key={ritual.id} 
                      className="paper-card p-5 rounded-xl hover:shadow-md transition-shadow cursor-pointer group"
                      onClick={() => {
                        toast(
                          <div>
                            <p className="font-medium mb-1">{ritual.name}</p>
                            <p className="text-sm text-[#4A4744]">{ritual.description}</p>
                          </div>,
                          { duration: 6000 }
                        );
                      }}
                      data-testid={`ritual-recommendation-${ritual.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#A87C6D]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#A87C6D]/20 transition-colors">
                          <RitualIcon className="w-6 h-6 text-[#A87C6D]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#2D2A26]">{ritual.name}</p>
                          <p className="text-sm text-[#6E6A66]">{ritual.duration} • Tap for details</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <p className="text-xs text-[#6E6A66] text-center mt-3 italic">
                  Based on how you're feeling today
                </p>
              </div>
            )}

            {/* No Ritual Normalization - show when redirect is empty */}
            {(!aiResponse.sections?.redirect || !aiResponse.sections.redirect.trim() || 
              aiResponse.sections.redirect.toLowerCase().includes('none needed') ||
              aiResponse.sections.redirect.toLowerCase().includes('no ritual')) && (
              <div className="animate-slide-up delay-400 mb-8 text-center py-6" style={{ animationFillMode: 'forwards', opacity: 0 }}>
                <p className="text-[#6E6A66] font-light italic">
                  We don't need to add anything here. Stillness counts.
                </p>
              </div>
            )}

            {/* Gentle Closing Message */}
            <div className="text-center py-6 border-t border-[#E6E2DD] animate-slide-up delay-500" style={{ animationFillMode: 'forwards', opacity: 0 }}>
              <p 
                className="text-lg text-[#A87C6D] font-light italic mb-2"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {getRandomClosingMessage()}
              </p>
            </div>

            {/* Affirmation Offer - Premium Feature */}
            {subscriptionStatus && subscriptionStatus.plan !== 'free' && !affirmationOffered && !affirmationLoading && (
              <div className="mb-6 p-5 rounded-2xl bg-[#7A9E7E]/5 border border-[#7A9E7E]/20 animate-slide-up delay-500" style={{ animationFillMode: 'forwards', opacity: 0 }}>
                <div className="text-center">
                  <Sparkles className="w-6 h-6 text-[#7A9E7E] mx-auto mb-3" />
                  <p 
                    className="text-[#2D2A26] font-light mb-4"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    Would you like a gentle affirmation to carry with you?
                  </p>
                  <div className="flex justify-center gap-3">
                    <Button
                      onClick={() => fetchAffirmation(Object.values(responses).join(' '))}
                      className="rounded-full px-5 py-2 bg-[#7A9E7E] hover:bg-[#6a8e6e] text-white text-sm"
                      data-testid="accept-affirmation-btn"
                    >
                      Yes, please
                    </Button>
                    <Button
                      onClick={() => setAffirmationOffered(true)}
                      variant="ghost"
                      className="rounded-full px-5 py-2 text-[#4A4744] hover:bg-[#E6E2DD] text-sm"
                      data-testid="decline-affirmation-btn"
                    >
                      Not today
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Affirmation Loading */}
            {affirmationLoading && (
              <div className="mb-6 p-5 rounded-2xl bg-[#7A9E7E]/5 border border-[#7A9E7E]/20 animate-slide-up" style={{ animationFillMode: 'forwards' }}>
                <div className="text-center">
                  <Loader2 className="w-6 h-6 text-[#7A9E7E] mx-auto mb-3 animate-spin" />
                  <p className="text-[#4A4744] font-light text-sm">
                    Creating something just for you...
                  </p>
                </div>
              </div>
            )}

            {/* Affirmation Display */}
            {showAffirmationOffer && affirmation && (
              <div className="mb-6 p-5 rounded-2xl bg-[#7A9E7E]/5 border border-[#7A9E7E]/20 animate-fade-in">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#7A9E7E]" />
                    <span className="text-xs uppercase tracking-wider text-[#7A9E7E] font-medium">
                      Your affirmation
                    </span>
                  </div>
                  <Button
                    onClick={handleDeclineAffirmation}
                    variant="ghost"
                    size="icon"
                    className="w-6 h-6 text-[#6E6A66] hover:text-[#4A4744]"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <p 
                  className="text-[#2D2A26] font-light leading-relaxed italic text-center py-3"
                  style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.15rem' }}
                  data-testid="affirmation-text"
                >
                  "{affirmation}"
                </p>
                <div className="flex justify-center mt-4">
                  {!affirmationSaved ? (
                    <Button
                      onClick={handleSaveAffirmation}
                      variant="ghost"
                      className="rounded-full px-4 py-2 text-[#7A9E7E] hover:bg-[#7A9E7E]/10 text-sm"
                      data-testid="save-affirmation-btn"
                    >
                      <Bookmark className="w-4 h-4 mr-2" />
                      Save this affirmation
                    </Button>
                  ) : (
                    <p className="text-sm text-[#7A9E7E] flex items-center gap-2">
                      <Bookmark className="w-4 h-4 fill-current" />
                      Saved to your collection
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Gentle Premium Nudge - Only for free users near their limit */}
            {subscriptionStatus && subscriptionStatus.plan === 'free' && subscriptionStatus.check_ins_used >= 2 && (
              <div className="mb-6 p-4 rounded-2xl bg-[#A87C6D]/5 border border-[#A87C6D]/10 animate-slide-up delay-500" style={{ animationFillMode: 'forwards', opacity: 0 }}>
                <div className="flex items-center gap-3">
                  <Crown className="w-5 h-5 text-[#A87C6D] flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-[#4A4744]">
                      {subscriptionStatus.check_ins_used === subscriptionStatus.check_in_limit - 1 
                        ? "This was your last free check-in this week."
                        : `You have ${subscriptionStatus.check_in_limit - subscriptionStatus.check_ins_used} check-in${subscriptionStatus.check_in_limit - subscriptionStatus.check_ins_used === 1 ? '' : 's'} left this week.`
                      }
                      {' '}
                      <Link to="/subscription" className="text-[#A87C6D] hover:underline">
                        Upgrade for unlimited
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-4 justify-center pt-4 animate-slide-up delay-600" style={{ animationFillMode: 'forwards', opacity: 0 }}>
              {/* Primary: That's Enough for Now Button */}
              <Button
                onClick={() => navigate('/dashboard')}
                className="rounded-full px-8 py-5 bg-[#A87C6D] hover:bg-[#956d5f] text-white w-full sm:w-auto mx-auto"
                data-testid="thats-enough-btn"
              >
                That's enough for now
              </Button>
              
              {/* Secondary options */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="ghost"
                  className="rounded-full px-6 py-4 text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#F5F2ED]"
                  data-testid="return-dashboard-btn"
                >
                  Return to your space
                </Button>
                <Button
                  onClick={resetCheckIn}
                  variant="ghost"
                  className="rounded-full px-6 py-4 text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#F5F2ED]"
                  data-testid="new-checkin-btn"
                >
                  Begin another check-in
                </Button>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // Prompts state
  const currentPrompt = prompts[currentPromptIndex];

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col" data-testid="checkin-view">
      <nav className="px-6 py-4 border-b border-[#E6E2DD]">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => currentPromptIndex > 0 ? setCurrentPromptIndex(prev => prev - 1) : setStep("mode_select")}
            className="rounded-full text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]" 
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <span className="text-sm text-[#6E6A66]">
            {currentPromptIndex + 1} of {prompts.length}
          </span>
          <div className="w-20"></div>
        </div>
      </nav>

      <div className="px-6 py-2">
        <div className="max-w-2xl mx-auto">
          <div className="h-1 bg-[#E6E2DD] rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#A87C6D] transition-all duration-500 ease-out"
              style={{ width: `${((currentPromptIndex + 1) / prompts.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-xl animate-fade-in" key={currentPromptIndex}>
          <div className="relative mb-12">
            <h2 
              className="text-2xl md:text-3xl font-light text-[#2D2A26] text-center leading-relaxed"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="current-prompt"
            >
              {currentPrompt?.text}
            </h2>
            <button
              onClick={() => toggleSavePrompt(currentPrompt?.prompt_id)}
              className={`absolute -right-2 top-0 p-2 rounded-full transition-colors ${
                savedPromptIds.has(currentPrompt?.prompt_id)
                  ? "text-[#A87C6D]"
                  : "text-[#E6E2DD] hover:text-[#6E6A66]"
              }`}
              title={savedPromptIds.has(currentPrompt?.prompt_id) ? "Remove from saved" : "Save prompt"}
              data-testid="save-prompt-btn"
            >
              <Bookmark 
                className={`w-5 h-5 ${savedPromptIds.has(currentPrompt?.prompt_id) ? "fill-current" : ""}`} 
              />
            </button>
          </div>

          <div className="space-y-6">
            <Textarea
              value={currentResponse}
              onChange={(e) => setCurrentResponse(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Take your time..."
              className="w-full min-h-[150px] p-6 text-lg font-light border-[#E6E2DD] focus:border-[#A87C6D] focus:ring-[#A87C6D] rounded-2xl bg-white resize-none"
              data-testid="response-textarea"
              autoFocus
            />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <p className="text-sm text-[#6E6A66] hidden sm:block">
                  Press ⌘ + Enter to continue
                </p>
                <button
                  onClick={handleSkipPrompt}
                  disabled={skipping}
                  className="flex items-center gap-1.5 text-sm text-[#6E6A66] hover:text-[#4A4744] transition-colors disabled:opacity-50"
                  data-testid="skip-prompt-btn"
                >
                  {skipping ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <SkipForward className="w-4 h-4" />
                  )}
                  <span>Different prompt</span>
                </button>
              </div>
              <Button
                onClick={handleNext}
                disabled={submitting || !currentResponse.trim()}
                className="rounded-full px-8 py-5 bg-[#A87C6D] hover:bg-[#956d5f] text-white disabled:opacity-50"
                data-testid="next-btn"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : currentPromptIndex < prompts.length - 1 ? (
                  "Continue"
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Reflect
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
